<script setup lang="ts">
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, unref, watch } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  fetchComplianceRequirementsList,
  toggleComplianceRequirement,
  bulkToggleComplianceRequirements,
} from '@/_core/entities/report/api/report.endpoints';
import type { TComplianceRequirementListItem } from '@/_core/entities/report/models/types';
import { useToast } from '@/_core/shared/ui/AppToast/useToast';
import SearchInput from '@/_core/shared/ui/SearchInput/SearchInput.vue';

const TYPES = ['Catalog', 'Framework', 'Guideline', 'Regulation', 'Standard'];
const SECTORS = ['Finance', 'General', 'Government', 'Healthcare'];
const ENFORCEMENTS = ['Mandatory', 'Voluntary'];
const JURISDICTIONS = ['Global', 'European Union', 'India', 'Egypt'];

const instance = getCurrentInstance();
const integrationId = computed(() => String(unref(instance?.proxy.$route)?.params?.id ?? ''));

const searchInput = ref('');
const search = ref('');
let searchDebounce: ReturnType<typeof setTimeout> | null = null;
watch(searchInput, (value) => {
  if (searchDebounce) clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => {
    search.value = value;
  }, 300);
});
const filters = ref<{
  jurisdiction: string[];
  type: string[];
  sector: string[];
  enforcement: string[];
}>({ jurisdiction: [], type: [], sector: [], enforcement: [] });

const openDropdown = ref<string | null>(null);
const filtersRef = ref<HTMLElement | null>(null);

function onClickOutside(e: MouseEvent) {
  if (openDropdown.value && filtersRef.value && !filtersRef.value.contains(e.target as Node)) {
    openDropdown.value = null;
  }
}

onMounted(() => document.addEventListener('click', onClickOutside));
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside));

const queryClient = useQueryClient();
const toast = useToast();

const requirementsQuery = useQuery({
  queryKey: computed(() => [
    'complianceRequirementsList',
    integrationId.value,
    search.value,
    filters.value,
  ]),
  queryFn: () =>
    fetchComplianceRequirementsList({
      integrationId: integrationId.value,
      search: search.value,
      jurisdiction: filters.value.jurisdiction,
      type: filters.value.type,
      sector: filters.value.sector,
      enforcement: filters.value.enforcement,
    }),
});

const requirements = computed(() => requirementsQuery.data.value ?? []);
const isLoading = computed(() => requirementsQuery.isLoading.value);

const hasActiveSearchOrFilters = computed(
  () =>
    !!searchInput.value ||
    filters.value.jurisdiction.length > 0 ||
    filters.value.type.length > 0 ||
    filters.value.sector.length > 0 ||
    filters.value.enforcement.length > 0
);

function clearAll() {
  searchInput.value = '';
  search.value = '';
  filters.value = { jurisdiction: [], type: [], sector: [], enforcement: [] };
}

function toggleFilterValue(key: 'jurisdiction' | 'type' | 'sector' | 'enforcement', value: string) {
  const current = filters.value[key];
  filters.value = {
    ...filters.value,
    [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
  };
}

function isFilterActive(
  key: 'jurisdiction' | 'type' | 'sector' | 'enforcement',
  value: string
) {
  return filters.value[key].includes(value);
}

const toggleMutation = useMutation({
  mutationFn: ({ requirementId, enabled }: { requirementId: string; enabled: boolean }) =>
    toggleComplianceRequirement(integrationId.value, requirementId, enabled),
  onMutate: async ({ requirementId, enabled }) => {
    const queryKey = [
      'complianceRequirementsList',
      integrationId.value,
      search.value,
      filters.value,
    ];
    await queryClient.cancelQueries({ queryKey });
    const previous = queryClient.getQueryData<TComplianceRequirementListItem[]>(queryKey);
    if (previous) {
      queryClient.setQueryData<TComplianceRequirementListItem[]>(
        queryKey,
        previous.map((r) => (r.id === requirementId ? { ...r, isEnabled: enabled } : r))
      );
    }
    return { previous, queryKey };
  },
  onError: (_err, _vars, context) => {
    if (context?.previous) {
      queryClient.setQueryData(context.queryKey, context.previous);
    }
    toast.error({ message: 'Failed to update compliance scope' });
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['complianceSummary'] });
    queryClient.invalidateQueries({ queryKey: ['singleScanReport'] });
    queryClient.invalidateQueries({ queryKey: ['versionReport'] });
    queryClient.invalidateQueries({ queryKey: ['singleScanPage'] });
    queryClient.invalidateQueries({ queryKey: ['appVersionPage'] });
    queryClient.invalidateQueries({ queryKey: ['folders'] });
  },
});

function onToggleRequirement(req: TComplianceRequirementListItem) {
  toggleMutation.mutate({ requirementId: req.id, enabled: !req.isEnabled });
}

const allEnabled = computed(
  () => requirements.value.length > 0 && requirements.value.every((r) => r.isEnabled)
);

const bulkMutation = useMutation({
  mutationFn: ({ ids, enabled }: { ids: string[]; enabled: boolean }) =>
    bulkToggleComplianceRequirements(integrationId.value, ids, enabled),
  onMutate: async ({ ids, enabled }) => {
    const queryKey = [
      'complianceRequirementsList',
      integrationId.value,
      search.value,
      filters.value,
    ];
    await queryClient.cancelQueries({ queryKey });
    const previous = queryClient.getQueryData<TComplianceRequirementListItem[]>(queryKey);
    if (previous) {
      const idSet = new Set(ids);
      queryClient.setQueryData<TComplianceRequirementListItem[]>(
        queryKey,
        previous.map((r) => (idSet.has(r.id) ? { ...r, isEnabled: enabled } : r))
      );
    }
    return { previous, queryKey };
  },
  onError: (_err, _vars, context) => {
    if (context?.previous) {
      queryClient.setQueryData(context.queryKey, context.previous);
    }
    toast.error({ message: 'Failed to update compliance scope' });
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['complianceSummary'] });
    queryClient.invalidateQueries({ queryKey: ['singleScanReport'] });
    queryClient.invalidateQueries({ queryKey: ['versionReport'] });
    queryClient.invalidateQueries({ queryKey: ['singleScanPage'] });
    queryClient.invalidateQueries({ queryKey: ['appVersionPage'] });
    queryClient.invalidateQueries({ queryKey: ['folders'] });
  },
});

function onBulkToggle() {
  if (requirements.value.length === 0) return;
  const enabled = !allEnabled.value;
  bulkMutation.mutate({ ids: requirements.value.map((r) => r.id), enabled });
}

function toggleDropdown(name: string) {
  openDropdown.value = openDropdown.value === name ? null : name;
}

type TFilterKey = 'jurisdiction' | 'type' | 'sector' | 'enforcement';

const filterGroups: Array<{ label: string; key: TFilterKey; options: string[] }> = [
  { label: 'Jurisdiction', key: 'jurisdiction', options: JURISDICTIONS },
  { label: 'Type', key: 'type', options: TYPES },
  { label: 'Domain', key: 'sector', options: SECTORS },
  { label: 'Enforcement', key: 'enforcement', options: ENFORCEMENTS },
];

const TYPE_BADGE_CLASS: Record<string, string> = {
  Standard: 'tw:bg-[#e8f0ff] tw:text-[#3180f6]',
  Catalog: 'tw:bg-[#efe8ff] tw:text-[#7c4dff]',
  Guideline: 'tw:bg-[#e6f7ee] tw:text-[#1ea65a]',
  Framework: 'tw:bg-[#fff0e3] tw:text-[#e8821a]',
  Regulation: 'tw:bg-[#fde7ec] tw:text-[#e84a6e]',
};

function typeBadgeClass(type: string) {
  return TYPE_BADGE_CLASS[type] ?? 'tw:bg-black/5 tw:text-secondary';
}

const referrer = computed(() => {
  const q = unref(instance?.proxy.$route)?.query?.from;
  return typeof q === 'string' ? q : '';
});

function returnToReport() {
  if (referrer.value) {
    instance?.proxy.$router.push(referrer.value);
  } else {
    instance?.proxy.$router.back();
  }
}

const isReturnBarDismissed = ref(false);
const showReturnBar = computed(() => !!referrer.value && !isReturnBarDismissed.value);
</script>

<template>
  <div class="tw:bg-white tw:rounded-2xl tw:p-6 tw:flex-1">
    <div class="tw:flex tw:items-start tw:justify-between tw:gap-4 tw:flex-wrap">
      <div class="tw:max-w-2xl tw:min-w-0">
        <div class="tw:typography-h2">Compliance Scope</div>
        <div class="tw:mt-2 tw:text-secondary tw:typography-small">
          Choose which compliance documents are shown in reports for this app. Findings are mapped
          across the full compliance library, but only the selected documents appear in the report.
        </div>
      </div>

      <SearchInput v-model="searchInput" placeholder="Search..." />
    </div>

    <div ref="filtersRef" class="tw:mt-4 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
      <div v-for="group in filterGroups" :key="group.key" class="tw:relative">
        <button
          type="button"
          class="tw:px-3 tw:py-1.5 tw:rounded-full tw:bg-black/5 tw:typography-small tw:flex tw:items-center tw:gap-1"
          @click="toggleDropdown(group.key)"
        >
          {{ group.label }}
          <span v-if="filters[group.key].length > 0" class="tw:text-main-blue">
            ({{ filters[group.key].length }})
          </span>
          <span class="tw:text-secondary">▾</span>
        </button>
        <div
          v-if="openDropdown === group.key"
          class="tw:absolute tw:top-full tw:left-0 tw:mt-1 tw:bg-white tw:border tw:border-black/10 tw:rounded-md tw:shadow tw:py-1 tw:min-w-40 tw:z-10"
        >
          <button
            v-for="opt in group.options"
            :key="opt"
            type="button"
            class="tw:w-full tw:text-left tw:px-3 tw:py-1.5 tw:typography-small tw:hover:bg-black/5 tw:flex tw:items-center tw:gap-2"
            @click="toggleFilterValue(group.key, opt)"
          >
            <span
              class="tw:size-4 tw:rounded tw:border tw:border-black/20 tw:flex tw:items-center tw:justify-center"
              :class="{
                'tw:bg-main-blue tw:border-main-blue tw:text-white': isFilterActive(group.key, opt),
              }"
            >
              <span v-if="isFilterActive(group.key, opt)">✓</span>
            </span>
            {{ opt }}
          </button>
        </div>
      </div>

      <button
        v-if="hasActiveSearchOrFilters"
        type="button"
        class="tw:px-3 tw:py-1.5 tw:typography-small tw:text-main-blue"
        @click="clearAll"
      >
        Clear
      </button>
    </div>

    <div v-if="isLoading" class="tw:mt-8 tw:text-secondary tw:typography-small">Loading...</div>

    <div
      v-else-if="requirements.length === 0"
      class="tw:mt-12 tw:flex tw:flex-col tw:items-center tw:gap-3"
    >
      <div class="tw:typography-h2">No Matches Found</div>
      <div class="tw:text-secondary tw:typography-small">
        We couldn't find any documents matching your search
      </div>
      <button
        v-if="hasActiveSearchOrFilters"
        type="button"
        class="tw:mt-2 tw:px-4 tw:py-2 tw:bg-main-blue tw:text-white tw:rounded-md tw:typography-small"
        @click="clearAll"
      >
        Clear Search
      </button>
    </div>

    <div v-else class="tw:mt-6">
      <div class="tw:hidden tw:md:block tw:overflow-x-auto">
      <table class="tw:w-full tw:min-w-180 tw:table-fixed">
        <colgroup>
          <col style="width: 64px" />
          <col />
          <col style="width: 140px" />
          <col style="width: 140px" />
          <col style="width: 160px" />
          <col style="width: 120px" />
        </colgroup>
        <thead>
          <tr class="tw:text-left tw:text-secondary tw:typography-small">
            <th class="tw:py-2 tw:pr-4 tw:font-normal tw:align-middle">
              <button
                type="button"
                class="tw:relative tw:inline-flex tw:h-6 tw:w-11 tw:rounded-full tw:transition-colors tw:align-middle"
                :class="allEnabled ? 'tw:bg-vuln-low' : 'tw:bg-black/15'"
                :aria-pressed="allEnabled"
                :aria-label="allEnabled ? 'Disable all' : 'Enable all'"
                :disabled="bulkMutation.isPending.value || requirements.length === 0"
                @click="onBulkToggle"
              >
                <span
                  class="tw:absolute tw:top-0.5 tw:size-5 tw:bg-white tw:rounded-full tw:transition-all"
                  :class="allEnabled ? 'tw:left-5' : 'tw:left-0.5'"
                />
              </button>
            </th>
            <th class="tw:py-2 tw:pr-4 tw:font-normal tw:align-middle">Header</th>
            <th class="tw:py-2 tw:pr-4 tw:font-normal tw:align-middle">Jurisdiction</th>
            <th class="tw:py-2 tw:pr-4 tw:font-normal tw:align-middle">Enforcement</th>
            <th class="tw:py-2 tw:pr-4 tw:font-normal tw:align-middle">Domain</th>
            <th class="tw:py-2 tw:pr-4 tw:font-normal tw:align-middle">Type</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="req in requirements"
            :key="req.id"
            class="tw:border-t tw:border-black/5"
          >
            <td class="tw:py-3 tw:pr-4">
              <button
                type="button"
                class="tw:relative tw:inline-flex tw:h-6 tw:w-11 tw:rounded-full tw:transition-colors"
                :class="req.isEnabled ? 'tw:bg-vuln-low' : 'tw:bg-black/15'"
                :aria-pressed="req.isEnabled"
                :disabled="toggleMutation.isPending.value"
                @click="onToggleRequirement(req)"
              >
                <span
                  class="tw:absolute tw:top-0.5 tw:size-5 tw:bg-white tw:rounded-full tw:transition-all"
                  :class="req.isEnabled ? 'tw:left-5' : 'tw:left-0.5'"
                />
              </button>
            </td>
            <td class="tw:py-3 tw:pr-4 tw:typography-small tw:align-middle tw:max-w-80 tw:truncate">
              <a
                v-if="req.externalUrl"
                :href="req.externalUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="tw:text-[#021631] tw:no-underline tw:hover:underline"
              >
                {{ req.name }}
                <span class="tw:text-secondary" aria-hidden="true">↗</span>
              </a>
              <template v-else>{{ req.name }}</template>
            </td>
            <td class="tw:py-3 tw:pr-4 tw:typography-small tw:align-middle">{{ req.jurisdiction }}</td>
            <td class="tw:py-3 tw:pr-4 tw:typography-small tw:align-middle">
              <span
                v-if="req.enforcement === 'Mandatory'"
                class="tw:inline-flex tw:items-center tw:gap-1 tw:text-[#e84a2a]"
              >
                <span aria-hidden="true">⚠</span>
                Mandatory
              </span>
              <span v-else class="tw:text-secondary">{{ req.enforcement }}</span>
            </td>
            <td class="tw:py-3 tw:pr-4 tw:typography-small tw:align-middle">
              <span class="tw:inline-flex tw:items-center tw:gap-1.5 tw:text-secondary">
                <span aria-hidden="true">📖</span>
                {{ req.sector }}
              </span>
            </td>
            <td class="tw:py-3 tw:pr-4 tw:typography-small tw:align-middle">
              <span class="tw:px-2 tw:py-0.5 tw:rounded-md" :class="typeBadgeClass(req.type)">
                {{ req.type }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      <div class="tw:flex tw:flex-col tw:gap-3 tw:md:hidden">
        <div class="tw:flex tw:items-center tw:justify-between tw:px-1">
          <span class="tw:typography-small tw:text-secondary">
            {{ allEnabled ? 'Disable all' : 'Enable all' }}
          </span>
          <button
            type="button"
            class="tw:relative tw:inline-flex tw:h-6 tw:w-11 tw:rounded-full tw:transition-colors"
            :class="allEnabled ? 'tw:bg-vuln-low' : 'tw:bg-black/15'"
            :aria-pressed="allEnabled"
            :aria-label="allEnabled ? 'Disable all' : 'Enable all'"
            :disabled="bulkMutation.isPending.value || requirements.length === 0"
            @click="onBulkToggle"
          >
            <span
              class="tw:absolute tw:top-0.5 tw:size-5 tw:bg-white tw:rounded-full tw:transition-all"
              :class="allEnabled ? 'tw:left-5' : 'tw:left-0.5'"
            />
          </button>
        </div>
        <div
          v-for="req in requirements"
          :key="req.id"
          class="tw:border tw:border-black/5 tw:rounded-xl tw:p-4 tw:flex tw:flex-col tw:gap-3"
        >
          <div class="tw:flex tw:items-start tw:justify-between tw:gap-3">
            <div class="tw:min-w-0 tw:flex tw:flex-col tw:gap-1">
              <a
                v-if="req.externalUrl"
                :href="req.externalUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="tw:typography-small tw:font-medium tw:break-words tw:text-[#021631] tw:no-underline tw:hover:underline"
              >
                {{ req.name }}
                <span class="tw:text-secondary tw:font-normal" aria-hidden="true">↗</span>
              </a>
              <span v-else class="tw:typography-small tw:font-medium tw:break-words">{{ req.name }}</span>
              <span
                class="tw:inline-block tw:self-start tw:px-2 tw:py-0.5 tw:rounded-md tw:typography-sidenotes"
                :class="typeBadgeClass(req.type)"
              >
                {{ req.type }}
              </span>
            </div>
            <button
              type="button"
              class="tw:relative tw:inline-flex tw:h-6 tw:w-11 tw:shrink-0 tw:rounded-full tw:transition-colors"
              :class="req.isEnabled ? 'tw:bg-vuln-low' : 'tw:bg-black/15'"
              :aria-pressed="req.isEnabled"
              :disabled="toggleMutation.isPending.value"
              @click="onToggleRequirement(req)"
            >
              <span
                class="tw:absolute tw:top-0.5 tw:size-5 tw:bg-white tw:rounded-full tw:transition-all"
                :class="req.isEnabled ? 'tw:left-5' : 'tw:left-0.5'"
              />
            </button>
          </div>
          <dl class="tw:grid tw:grid-cols-2 tw:gap-x-3 tw:gap-y-2 tw:typography-sidenotes">
            <div class="tw:flex tw:flex-col">
              <dt class="tw:text-secondary">Jurisdiction</dt>
              <dd class="tw:typography-small">{{ req.jurisdiction }}</dd>
            </div>
            <div class="tw:flex tw:flex-col">
              <dt class="tw:text-secondary">Enforcement</dt>
              <dd class="tw:typography-small">
                <span
                  v-if="req.enforcement === 'Mandatory'"
                  class="tw:inline-flex tw:items-center tw:gap-1 tw:text-[#e84a2a]"
                >
                  <span aria-hidden="true">⚠</span>
                  Mandatory
                </span>
                <span v-else class="tw:text-secondary">{{ req.enforcement }}</span>
              </dd>
            </div>
            <div class="tw:flex tw:flex-col tw:col-span-2">
              <dt class="tw:text-secondary">Domain</dt>
              <dd class="tw:typography-small tw:inline-flex tw:items-center tw:gap-1.5">
                <span aria-hidden="true">📖</span>
                {{ req.sector }}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>

    <div
      v-if="showReturnBar"
      class="tw:fixed tw:bottom-6 tw:left-1/2 tw:-translate-x-1/2 tw:bg-black tw:text-white tw:rounded-full tw:px-4 tw:py-2 tw:flex tw:items-center tw:gap-3 tw:shadow-lg tw:z-20"
    >
      <button type="button" class="tw:typography-small tw:flex tw:items-center tw:gap-2" @click="returnToReport">
        <span aria-hidden="true">←</span>
        Return to Report
      </button>
      <button
        type="button"
        class="tw:text-secondary tw:hover:text-white tw:typography-small"
        aria-label="Dismiss"
        @click="isReturnBarDismissed = true"
      >
        ✕
      </button>
    </div>
  </div>
</template>
